"use client";

import { useMemo } from "react";
import { useStore } from "@/store";
import {
  resolveSessionMediaProvider,
  type SharedMediaProvider,
} from "@/lib/adminSession";
import {
  getSessionMediaProviderAdapter,
  type SessionMediaProviderAdapter,
} from "@/lib/sessionMediaProvider";

export type SessionMediaProviderState = {
  provider: SharedMediaProvider | null;
  adapter: SessionMediaProviderAdapter | null;
  label: string;
  hasMediaProvider: boolean;
  isSpotifyProvider: boolean;
  isYouTubeProvider: boolean;
  isAppleMusicProvider: boolean;
};

export function buildSessionMediaProviderState(
  userProvider: string | null | undefined,
): SessionMediaProviderState {
  const provider = resolveSessionMediaProvider(
    userProvider ?? null,
    null,
    "participant",
  );
  const adapter = getSessionMediaProviderAdapter(provider);

  return {
    provider,
    adapter,
    label: adapter?.label ?? "Media",
    hasMediaProvider: Boolean(adapter),
    isSpotifyProvider: provider === "spotify",
    isYouTubeProvider: provider === "youtube",
    isAppleMusicProvider: provider === "applemusic",
  };
}

export function useSessionMediaProvider(userProviderOverride?: string | null) {
  const storeUserProvider = useStore.use.user()?.provider ?? null;
  const resolvedUserProvider =
    userProviderOverride === undefined ? storeUserProvider : userProviderOverride;

  return useMemo(
    () => buildSessionMediaProviderState(resolvedUserProvider),
    [resolvedUserProvider],
  );
}
